import { apiClient } from "./client";
import { PaginationMeta } from "../lib/bufferedPagination";

export type AdminRoleRef = {
  _id: string;
  name: string;
  description?: string;
  permissions?: string[];
};

export type AdminRecord = {
  _id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  isProfileCompleted?: boolean;
  role?: string;
  roles?: AdminRoleRef[];
  additionalPermissions?: string[];
  businesses?: Array<{
    _id: string;
    name?: string;
    registrationNumber?: string;
    address?: string;
    country?: string;
    status?: string;
    email?: string;
    stage?: number;
  }>;
  createdAt?: string;
  updatedAt?: string;
};

export type GetAdminsParams = {
  roleIds?: string;
  startDate?: string;
  endDate?: string;
  isProfileCompleted?: boolean;
  limit?: number;
  sort?: string;
  q?: string;
  page?: number;
};

export type InviteMembersPayload = {
  emails: string[];
  roleIds: string[];
  additionalPermissions: string[];
};

export type UpdateAdminRolePayload = {
  roleIds?: string[];
  additionalPermissions?: string[];
};

export type RemoveAdminRolePayload = {
  roleIds: string[];
};

export type AssignCustomersPayload = {
  adminId: string;
  businessIds: string[];
};

export type ReassignCustomersPayload = {
  fromAdminId: string;
  toAdminId: string;
  businessIds: string[];
};

type AdminsResponse = {
  data?: AdminRecord[];
  admins?: AdminRecord[];
  items?: AdminRecord[];
  results?: AdminRecord[];
  meta?: PaginationMeta;
};

export type AdminsResult = {
  items: AdminRecord[];
  meta?: PaginationMeta;
};

const normalizeAdmins = (payload: AdminsResponse | AdminRecord[]): AdminsResult => {
  if (Array.isArray(payload)) {
    return { items: payload };
  }

  return {
    items: payload.data ?? payload.admins ?? payload.items ?? payload.results ?? [],
    meta: payload.meta,
  };
};

export const getAdmins = async (params: GetAdminsParams) => {
  const response = await apiClient.get<AdminsResponse | AdminRecord[]>(
    "/v1/admin/admins/all",
    { params },
  );

  return normalizeAdmins(response.data ?? []);
};

export const inviteMembers = async (payload: InviteMembersPayload) => {
  await apiClient.post("/v1/admin/create", payload);
};

export const resendInvite = async (adminId: string) => {
  await apiClient.post(`/v1/admin/${adminId}/resend-invite`);
};

export const updateAdminRoleAndPermissions = async (
  adminId: string,
  payload: UpdateAdminRolePayload,
) => {
  await apiClient.put(`/v1/admin/${adminId}/update-role`, payload);
};

export const removeAdminRole = async (
  adminId: string,
  payload: RemoveAdminRolePayload,
) => {
  await apiClient.delete(`/v1/admin/${adminId}/remove-role`, {
    data: payload,
  });
};

export const assignCustomersToAdmin = async (payload: AssignCustomersPayload) => {
  await apiClient.post("/v1/admin/customers", payload);
};

export const reassignCustomers = async (payload: ReassignCustomersPayload) => {
  await apiClient.put("/v1/admin/customers", payload);
};

export const deleteAdminProfile = async (adminId: string) => {
  await apiClient.delete(`/v1/admin/${adminId}/delete`);
};
